import { apiErrorSchema, type ApiErrorCode, type RefreshResponse } from '@fruitways/shared';

/** Thrown for any non-2xx response the server described. `code` maps to copy in messages. */
export class ApiRequestError extends Error {
  constructor(
    readonly code: ApiErrorCode,
    readonly status: number,
    message: string,
    readonly fields?: Record<string, string>,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

/** Thrown when the request never reached the server or no response came back. */
export class NetworkError extends Error {
  constructor(cause?: unknown) {
    super('Network unavailable', { cause });
    this.name = 'NetworkError';
  }
}

let accessToken: string | null = null;
let refreshing: Promise<boolean> | null = null;
const listeners = new Set<(signedIn: boolean) => void>();

export function setAccessToken(token: string | null): void {
  accessToken = token;
}
export function hasAccessToken(): boolean {
  return accessToken !== null;
}
/** Fires with `false` when a refresh is refused, i.e. the session is over. */
export function onSessionChange(fn: (signedIn: boolean) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Ask the server for a new access token using the refresh cookie. Deduplicated. */
export async function refreshSession(): Promise<boolean> {
  if (!refreshing) {
    refreshing = (async () => {
      let res: Response;
      try {
        res = await fetch('/api/auth/refresh', { method: 'POST', credentials: 'same-origin' });
      } catch (err) {
        throw new NetworkError(err);
      }
      if (!res.ok) {
        accessToken = null;
        listeners.forEach((l) => l(false));
        return false;
      }
      const body = (await res.json()) as RefreshResponse;
      accessToken = body.accessToken;
      return true;
    })().finally(() => {
      refreshing = null;
    });
  }
  return refreshing;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  /** Skip bearer auth and the refresh dance (login, forgot password). */
  anonymous?: boolean;
  signal?: AbortSignal;
}

async function rawRequest(path: string, opts: RequestOptions): Promise<Response> {
  const headers: Record<string, string> = { accept: 'application/json' };
  if (opts.body !== undefined) headers['content-type'] = 'application/json';
  if (!opts.anonymous && accessToken) headers['authorization'] = `Bearer ${accessToken}`;
  try {
    return await fetch(path, {
      method: opts.method ?? 'GET',
      headers,
      body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
      credentials: 'same-origin',
      signal: opts.signal,
    });
  } catch (err) {
    throw new NetworkError(err);
  }
}

/**
 * JSON request with one transparent retry after a 401 via the refresh cookie.
 * Resolves `undefined` for 204.
 */
export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  let res = await rawRequest(path, opts);
  if (res.status === 401 && !opts.anonymous) {
    const ok = await refreshSession();
    if (ok) res = await rawRequest(path, opts);
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const json: unknown = text ? JSON.parse(text) : undefined;
  if (!res.ok) {
    const parsed = apiErrorSchema.safeParse(json);
    if (parsed.success) {
      const e = parsed.data.error;
      throw new ApiRequestError(e.code, res.status, e.message, e.fields);
    }
    throw new ApiRequestError('internal', res.status, `HTTP ${res.status}`);
  }
  return json as T;
}
